import { Controller } from 'stimulus';
import Hls from 'hls.js';

// Plays an inline video block on the post page (Block::Newsletter::VideoComponent#render_live). The cover
// (poster, play button, duration) is what shows until play; nothing is fetched from the CDN before then,
// unless the creator turned on muted autoplay. The server only renders this for viewers cleared to read the
// block, with signed playback URLs: an HLS master (CloudFront signed cookies, so requests carry
// credentials) and an MP4 rendition as the fallback.
export default class extends Controller {
  static targets = ['video', 'cover'];
  static values = { hlsUrl: String, mp4Url: String, autoplay: Boolean };

  connect() {
    if (this.autoplayValue) this.start({ muted: true });
  }

  disconnect() {
    this.destroyHls();
  }

  play() {
    this.start({ muted: false });
  }

  start({ muted }) {
    if (this.started) return;
    this.started = true;

    const video = this.videoTarget;
    video.muted = muted;
    if (muted) video.loop = true;
    video.controls = true;
    this.coverTarget.classList.add('hidden');

    this.load(video);
    const attempt = video.play();
    if (attempt?.catch) {
      attempt.catch((error) => {
        // An AbortError means the source was swapped under a pending play() (the MP4 fallback), which is playing.
        if (error?.name !== 'NotAllowedError') return;

        // Playback was blocked: show the cover again, and let the next press start from a clean player.
        this.destroyHls();
        delete video.dataset.fellBack;
        this.coverTarget.classList.remove('hidden');
        this.started = false;
      });
    }
  }

  load(video) {
    if (this.hlsUrlValue && Hls.isSupported()) {
      this.hls = new Hls({
        capLevelToPlayerSize: true,
        xhrSetup: xhr => { xhr.withCredentials = true; },
      });
      this.hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) this.fallBackToMp4(video);
      });
      this.hls.loadSource(this.hlsUrlValue);
      this.hls.attachMedia(video);
    } else if (this.hlsUrlValue && video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = this.hlsUrlValue;
      video.addEventListener('error', () => this.fallBackToMp4(video), { once: true });
    } else {
      this.fallBackToMp4(video);
    }
  }

  fallBackToMp4(video) {
    this.destroyHls();
    if (!this.mp4UrlValue || video.dataset.fellBack) return;

    video.dataset.fellBack = 'true';
    const resumeAt = video.currentTime;
    video.src = this.mp4UrlValue;
    if (resumeAt > 0) video.currentTime = resumeAt;
    const attempt = video.play();
    if (attempt?.catch) attempt.catch(() => {});
  }

  destroyHls() {
    if (!this.hls) return;
    this.hls.destroy();
    this.hls = null;
  }
}
